import { nameof } from 'ts-simple-nameof'

type Selector<T> = (selector: T) => any

enum Order {
  ASCENDING = 'ASC',
  DESCENDING = 'DESC'
}

export interface ISortOrder<T> {
  ascending(selector: Selector<T>): this
  descending(selector: Selector<T>): this
  toString(): string
}

class SortOrder<T extends object> implements ISortOrder<T> {
  private sortOrder: string[] = []

  private addSortOrder(selector: Selector<T>, order: Order): void {
    this.sortOrder.push(`${nameof(selector)}|${order}`)
  }

  /**
   * Adds an ascending sort order for the given property selector.
   */
  ascending(selector: Selector<T>): this {
    this.addSortOrder(selector, Order.ASCENDING)

    return this
  }

  /**
   * Adds an descending sort order for the given property selector.
   */
  descending(selector: Selector<T>): this {
    this.addSortOrder(selector, Order.DESCENDING)

    return this
  }

  /**
   * Returns the stringified version of the sort order ready to be used as an order by parameter.
   */
  toString(): string {
    return this.sortOrder.join(',')
  }
}

/**
 * Sort order helper for List
 *
 * @example
 * interface AccountList {
 *   Account: {
 *     Age: number
 *     FirstName: string
 *   }
 * }
 *
 * // This results in a sort order on first name ascending
 * // and age descending.
 * const order = sort<AccountList>()
 *   .ascending(o => o.Account.FirstName)
 *   .descending(o => o.Account.Age)
 *   .toString()
 */
export function sort<T extends object>(): ISortOrder<T> {
  return new SortOrder()
}
